// Mock API service for InsiderPass
export interface Member {
  memberId: number;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email?: string;
  currentPoints: number;
  lifetimePoints: number;
  currentTier: 'Bronze' | 'Silver' | 'Gold';
  enrolledDate: string;
  shopifyCustomerId?: string | null;
}

export interface Reward {
  rewardId: number;
  rewardName: string;
  rewardDescription: string;
  rewardCategory: 'Access' | 'Experience' | 'Discount' | 'Service';
  rewardType: 'insider_perk' | 'points_reward';
  pointsCost: number;
  minTierRequired: 'Bronze' | 'Silver' | 'Gold';
  requiresScheduling: boolean;
  advanceNoticeDays?: number;
  availableQuantity?: number;
  remainingQuantity?: number;
  isEligible: boolean;
  canAfford: boolean;
}

export interface Redemption {
  redemptionId: number;
  rewardId: number;
  rewardName: string;
  rewardDescription?: string;
  pointsRedeemed: number;
  redemptionCode: string;
  redemptionStatus: 'Confirmed' | 'Completed' | 'Cancelled';
  createdDate: string;
  confirmedDate?: string;
  fulfilledDate?: string;
  instructions?: string;
  canCancel: boolean;
}

const mockMember: Member = {
  memberId: 1,
  phoneNumber: '+15551234567',
  firstName: 'Chris',
  lastName: 'Jones',
  email: 'chris@insiderpass.io',
  currentPoints: 150,
  lifetimePoints: 150,
  currentTier: 'Bronze',
  enrolledDate: '2024-01-15T00:00:00Z',
  shopifyCustomerId: 'gid://shopify/Customer/7654321098765'
};

const mockRewards: Reward[] = [
  {
    rewardId: 1,
    rewardName: 'Birthday Month Bonus',
    rewardDescription: 'Get an extra 10% off all purchases during your birthday month',
    rewardCategory: 'Discount',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Bronze',
    requiresScheduling: false,
    isEligible: true,
    canAfford: true
  },
  {
    rewardId: 2,
    rewardName: 'Extended Returns',
    rewardDescription: '30-day return window instead of standard 14 days',
    rewardCategory: 'Service',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Bronze',
    requiresScheduling: false,
    isEligible: true,
    canAfford: true
  },
  {
    rewardId: 3,
    rewardName: 'Early Shopping Hours',
    rewardDescription: 'Shop one hour before store opening every Tuesday',
    rewardCategory: 'Access',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Silver',
    requiresScheduling: false,
    isEligible: false,
    canAfford: false
  },
  {
    rewardId: 4,
    rewardName: 'Reserved VIP Parking',
    rewardDescription: 'Dedicated parking spot near the entrance at all locations',
    rewardCategory: 'Access',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Silver',
    requiresScheduling: false,
    isEligible: false,
    canAfford: false
  },
  {
    rewardId: 5,
    rewardName: 'Warehouse Tour',
    rewardDescription: 'Behind-the-scenes tour of our liquidation warehouse',
    rewardCategory: 'Experience',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Gold',
    requiresScheduling: true,
    advanceNoticeDays: 7,
    isEligible: false,
    canAfford: false
  },
  {
    rewardId: 6,
    rewardName: 'Personal Shopper Session',
    rewardDescription: '1-hour dedicated assistance from our expert staff',
    rewardCategory: 'Service',
    rewardType: 'insider_perk',
    pointsCost: 0,
    minTierRequired: 'Gold',
    requiresScheduling: true,
    advanceNoticeDays: 3,
    isEligible: false,
    canAfford: false
  },
  {
    rewardId: 7,
    rewardName: 'Free Item Under $20',
    rewardDescription: 'Choose any item valued at $20 or less',
    rewardCategory: 'Discount',
    rewardType: 'points_reward',
    pointsCost: 100,
    minTierRequired: 'Bronze',
    requiresScheduling: false,
    availableQuantity: 50,
    remainingQuantity: 42,
    isEligible: true,
    canAfford: false
  },
  {
    rewardId: 8,
    rewardName: 'Concert Tickets (2)',
    rewardDescription: 'Two tickets to select local concerts and events',
    rewardCategory: 'Experience',
    rewardType: 'points_reward',
    pointsCost: 500,
    minTierRequired: 'Silver',
    requiresScheduling: true,
    advanceNoticeDays: 14,
    availableQuantity: 10,
    remainingQuantity: 3,
    isEligible: false,
    canAfford: false
  },
  {
    rewardId: 9,
    rewardName: '$50 Gift Card',
    rewardDescription: 'BuggyBusters gift card valid at all locations',
    rewardCategory: 'Discount',
    rewardType: 'points_reward',
    pointsCost: 250,
    minTierRequired: 'Bronze',
    requiresScheduling: false,
    availableQuantity: 100,
    remainingQuantity: 87,
    isEligible: true,
    canAfford: false
  }
];

let mockRedemptions: Redemption[] = [];

class MockApiService {
  private delay(ms: number = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    const member = localStorage.getItem('currentMember');
    return !!(token && member);
  }

  async signup(phoneNumber: string, firstName: string, lastName: string, email?: string) {
    await this.delay();
    
    const registeredPhones = JSON.parse(localStorage.getItem('registeredPhones') || '[]');
    if (registeredPhones.includes(phoneNumber)) {
      throw new Error('This phone number is already registered. Please log in instead.');
    }

    sessionStorage.setItem('pendingSignup', JSON.stringify({ phoneNumber, firstName, lastName, email }));
    return { success: true, phoneNumber };
  }

  async login(phoneNumber: string) {
    await this.delay();
    
    const registeredPhones = JSON.parse(localStorage.getItem('registeredPhones') || '[]');
    if (!registeredPhones.includes(phoneNumber)) {
      throw new Error('Phone number not found. Please sign up first.');
    }

    sessionStorage.setItem('loginPhone', phoneNumber);
    return { success: true, phoneNumber };
  }

  async verifyPhone(phoneNumber: string, code: string) {
    await this.delay();

    if (code !== '123456') {
      throw new Error('Invalid verification code');
    }

    const pendingSignup = sessionStorage.getItem('pendingSignup');
    if (!pendingSignup) {
      throw new Error('No pending signup found');
    }

    const { firstName, lastName, email } = JSON.parse(pendingSignup);
    
    const member: Member = {
      ...mockMember,
      phoneNumber,
      firstName,
      lastName,
      email
    };

    localStorage.setItem('authToken', 'mock-jwt-token-' + Date.now());
    localStorage.setItem('currentMember', JSON.stringify(member));
    localStorage.setItem(`member_${phoneNumber}`, JSON.stringify(member));
    
    const registeredPhones = JSON.parse(localStorage.getItem('registeredPhones') || '[]');
    if (!registeredPhones.includes(phoneNumber)) {
      registeredPhones.push(phoneNumber);
      localStorage.setItem('registeredPhones', JSON.stringify(registeredPhones));
    }

    sessionStorage.removeItem('pendingSignup');

    return { success: true, member, token: 'mock-jwt-token' };
  }

  async verifyLogin(phoneNumber: string, code: string) {
    await this.delay();

    if (code !== '123456') {
      throw new Error('Invalid verification code');
    }

    const memberData = localStorage.getItem(`member_${phoneNumber}`);
    if (!memberData) {
      throw new Error('Member not found');
    }

    const member = JSON.parse(memberData);
    
    localStorage.setItem('authToken', 'mock-jwt-token-' + Date.now());
    localStorage.setItem('currentMember', JSON.stringify(member));
    sessionStorage.removeItem('loginPhone');

    return { success: true, member, token: 'mock-jwt-token' };
  }

  async getCurrentMember(): Promise<Member> {
    await this.delay(300);
    
    const memberData = localStorage.getItem('currentMember');
    if (!memberData) {
      throw new Error('Not authenticated');
    }

    return JSON.parse(memberData);
  }

  async getRewardsCatalog() {
    await this.delay();
    
    const member = await this.getCurrentMember();
    
    const rewardsWithEligibility = mockRewards.map(reward => ({
      ...reward,
      isEligible: this.checkTierEligibility(member.currentTier, reward.minTierRequired),
      canAfford: this.checkTierEligibility(member.currentTier, reward.minTierRequired) && 
                 member.currentPoints >= reward.pointsCost
    }));

    return { rewards: rewardsWithEligibility };
  }

  async getReward(rewardId: string): Promise<Reward> {
    await this.delay();
    
    const member = await this.getCurrentMember();
    const reward = mockRewards.find(r => r.rewardId === parseInt(rewardId));
    
    if (!reward) {
      throw new Error('Reward not found');
    }

    return {
      ...reward,
      isEligible: this.checkTierEligibility(member.currentTier, reward.minTierRequired),
      canAfford: this.checkTierEligibility(member.currentTier, reward.minTierRequired) && 
                 member.currentPoints >= reward.pointsCost
    };
  }

  async redeemReward(rewardId: number, requestedDate?: string) {
    await this.delay();
    
    const member = await this.getCurrentMember();
    const reward = mockRewards.find(r => r.rewardId === rewardId);
    
    if (!reward) {
      throw new Error('Reward not found');
    }

    if (!this.checkTierEligibility(member.currentTier, reward.minTierRequired)) {
      throw new Error(`This reward requires ${reward.minTierRequired} tier`);
    }

    if (member.currentPoints < reward.pointsCost) {
      throw new Error('Insufficient points');
    }

    const redemption: Redemption = {
      redemptionId: Date.now(),
      rewardId: reward.rewardId,
      rewardName: reward.rewardName,
      rewardDescription: reward.rewardDescription,
      pointsRedeemed: reward.pointsCost,
      redemptionCode: this.generateRedemptionCode(),
      redemptionStatus: 'Confirmed',
      createdDate: new Date().toISOString(),
      confirmedDate: requestedDate,
      instructions: reward.requiresScheduling 
        ? `Please arrive 15 minutes early on ${requestedDate ? new Date(requestedDate).toLocaleDateString() : 'your scheduled date'}`
        : 'Show this code at checkout to redeem your reward',
      canCancel: true
    };

    mockRedemptions.unshift(redemption);
    localStorage.setItem('myRedemptions', JSON.stringify(mockRedemptions));

    member.currentPoints -= reward.pointsCost;
    localStorage.setItem('currentMember', JSON.stringify(member));

    return {
      success: true,
      redemption,
      memberPointsRemaining: member.currentPoints
    };
  }

  async getMyRedemptions() {
    await this.delay();
    
    const stored = localStorage.getItem('myRedemptions');
    const redemptions = stored ? JSON.parse(stored) : mockRedemptions;

    return { redemptions };
  }

  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentMember');
  }

  private checkTierEligibility(memberTier: string, requiredTier: string): boolean {
    const tierOrder = { 'Bronze': 1, 'Silver': 2, 'Gold': 3 };
    return tierOrder[memberTier as keyof typeof tierOrder] >= tierOrder[requiredTier as keyof typeof tierOrder];
  }

  private generateRedemptionCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }
}

export const mockApi = new MockApiService();
